import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Flex,
  Text,
  Badge,
  useDisclosure,
  Icon,
  HStack,
} from '@chakra-ui/react';
import { MdPhone, MdPhoneEnabled, MdPhoneDisabled, MdCallMade } from 'react-icons/md';
import * as JsSIP from 'jssip';
import { useCalls } from 'contexts/CallsContext';
import config, { BASE_URL } from 'config/api';
import LinkCallToMissionModal from './LinkCallToMissionModal';

function getPbxWsUrl() {
  const cfg = config.pbx;
  if (cfg.wsUrl) return cfg.wsUrl;
  const protocol = cfg.useWss ? 'wss' : 'ws';
  return `${protocol}://${cfg.host}:${cfg.wsPort}/ws`;
}

export default function PhoneWidget() {
  const toast = useToast();
  const callsContext = useCalls();
  const { isOpen: isPhoneModalOpen, onOpen: onPhoneModalOpen, onClose: onPhoneModalClose } = useDisclosure();
  const { isOpen: isCallModalOpen, onOpen: onCallModalOpen, onClose: onCallModalClose } = useDisclosure();
  const { isOpen: isLinkMissionModalOpen, onOpen: onLinkMissionModalOpen, onClose: onLinkMissionModalClose } = useDisclosure();
  
  // États pour la liaison mission
  const [currentCallUniqueid, setCurrentCallUniqueid] = useState(null);
  const [currentCallerId, setCurrentCallerId] = useState(null);
  const [callAudioPath, setCallAudioPath] = useState(null); // Chemin du fichier audio enregistré
  const currentCallUniqueidRef = useRef(null); // Ref pour accès immédiat à l'uniqueid
  
  // États pour la connexion téléphone
  const [phoneExtension, setPhoneExtension] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('softphone_extension') || '101';
    }
    return '101';
  });
  const [sipDomain, setSipDomain] = useState('');
  const [isPhoneConnected, setIsPhoneConnected] = useState(false);
  const [sipRegistered, setSipRegistered] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  
  // États pour le softphone WebRTC
  const [sipUA, setSipUA] = useState(null);
  const [currentSipSession, setCurrentSipSession] = useState(null);
  const remoteAudioRef = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const incomingBeepRef = useRef(null);
  const ringAudioRef = useRef(null);
  const ringPlayingRef = useRef(false);
  
  // États pour l'enregistrement
  const callRecordingRef = useRef(null);
  const callRecordingChunksRef = useRef([]);
  const callRecordingStreamRef = useRef(null);

  // Synchroniser le channel AMI depuis CallsContext
  useEffect(() => {
    if (incomingCall?.session && callsContext?.incomingCall?.channel) {
      if (!incomingCall.channel) {
        setIncomingCall(prev => ({
          ...prev,
          channel: callsContext.incomingCall.channel
        }));
      }
    }
  }, [callsContext?.incomingCall, incomingCall]);

  const stopRing = () => {
    try {
      if (ringAudioRef.current) {
        ringAudioRef.current.pause();
        ringAudioRef.current.currentTime = 0;
      }
    } catch (e) {}
    ringPlayingRef.current = false;
  };

  // Sonnerie (ring.mp3) pour les appels entrants (si téléphone connecté)
  useEffect(() => {
    const shouldRing = Boolean(isPhoneConnected && incomingCall && !currentSipSession);

    if (shouldRing && !ringPlayingRef.current) {
      try {
        if (!ringAudioRef.current) {
          const audio = new Audio('/ring.mp3');
          audio.loop = true;
          audio.preload = 'auto';
          ringAudioRef.current = audio;
        }

        ringAudioRef.current.currentTime = 0;
        const playPromise = ringAudioRef.current.play();
        ringPlayingRef.current = true;

        // Fallback si le navigateur bloque l'autoplay
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch((err) => {
            ringPlayingRef.current = false;
            console.warn('[PhoneWidget] Lecture ring.mp3 bloquée, fallback beep:', err);

            // Fallback: bip léger toutes les 2s
            if (!incomingBeepRef.current) {
              const playBeep = () => {
                try {
                  const AudioCtx = window.AudioContext || window.webkitAudioContext;
                  if (!AudioCtx) return;
                  const ctx = new AudioCtx();
                  const osc = ctx.createOscillator();
                  const gain = ctx.createGain();
                  osc.connect(gain);
                  gain.connect(ctx.destination);
                  osc.frequency.value = 440;
                  gain.gain.value = 0.2;
                  osc.start();
                  osc.stop(ctx.currentTime + 0.2);
                  osc.onended = () => {
                    try { ctx.close(); } catch (e) {}
                  };
                } catch (e) {
                  // no-op
                }
              };
              playBeep();
              incomingBeepRef.current = setInterval(playBeep, 2000);
            }
          });
        }
      } catch (e) {
        ringPlayingRef.current = false;
      }
    }

    if (!shouldRing) {
      stopRing();
      if (incomingBeepRef.current) {
        clearInterval(incomingBeepRef.current);
        incomingBeepRef.current = null;
      }
    }

    return () => {
      stopRing();
      if (incomingBeepRef.current) {
        clearInterval(incomingBeepRef.current);
        incomingBeepRef.current = null;
      }
    };
  }, [incomingCall, currentSipSession, isPhoneConnected]);

  // Fonction pour attacher les événements d'une session
  const attachSessionEvents = (session) => {
    const attachRemoteAudio = () => {
      try {
        const pc = session.connection;
        if (!pc) {
          console.warn('⚠️ PeerConnection non disponible');
          return;
        }

        const remoteStream = new MediaStream();
        pc.getReceivers().forEach((receiver) => {
          if (receiver.track && receiver.track.kind === 'audio') {
            remoteStream.addTrack(receiver.track);
          }
        });

        if (remoteAudioRef.current && remoteStream.getAudioTracks().length > 0) {
          remoteAudioRef.current.srcObject = remoteStream;
          remoteAudioRef.current.play().catch((err) => {
            console.error('❌ Erreur lecture audio:', err);
          });
        }
      } catch (err) {
        console.error('❌ Erreur attachement audio distant:', err);
      }
    };

    // Nettoyage SDP pour éviter le conflit G726-32
    session.on('sdp', (e) => {
      if (e.sdp && e.type === 'offer') {
        let sdp = e.sdp;
        const lines = sdp.split('\r\n');
        const filteredLines = lines.filter(line => {
          if (line.match(/^a=rtpmap:2\s+G726/i)) {
            return false;
          }
          return true;
        });

        e.sdp = filteredLines.map(line => {
          if (line.startsWith('m=audio')) {
            return line.replace(/\s+2(?=\s|$)/, '');
          }
          return line;
        }).join('\r\n');
      }
    });

    session.on('peerconnection', (e) => {
      const pc = e.peerconnection;
      
      pc.addEventListener('connectionstatechange', () => {
        const state = pc.connectionState;
        if (state === 'disconnected' || state === 'closed') {
          if (session.status !== 8) {
            setTimeout(() => {
              if (session.status !== 8) {
                stopCallRecording();
                if (remoteAudioRef.current) {
                  remoteAudioRef.current.srcObject = null;
                }
                setCurrentSipSession(prev => {
                  if (prev === session) return null;
                  return prev;
                });
                setIncomingCall(null);
              }
            }, 100);
          }
        }
      });

      pc.addEventListener('iceconnectionstatechange', () => {
        const iceState = pc.iceConnectionState;
        if (iceState === 'disconnected' || iceState === 'closed') {
          if (session.status !== 8) {
            setTimeout(() => {
              if (session.status !== 8) {
                stopCallRecording();
                if (remoteAudioRef.current) {
                  remoteAudioRef.current.srcObject = null;
                }
                setCurrentSipSession(prev => {
                  if (prev === session) return null;
                  return prev;
                });
                setIncomingCall(null);
              }
            }, 500);
          }
        }
      });
    });

    session.on('confirmed', () => {
      attachRemoteAudio();
      setTimeout(() => {
        startCallRecording(session);
      }, 1000);
    });

    session.on('ended', (e) => {
      stopRing();
      stopCallRecording();
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }
      
      // Sauvegarder l'uniqueid si l'appel se termine avant la création de mission
      saveCallUniqueidIfNeeded();
      
      setCurrentSipSession(null);
      setIncomingCall(null);
      // Ne pas réinitialiser le ref ici car saveCallRecording peut encore l'utiliser
    });

    session.on('failed', (e) => {
      stopRing();
      stopCallRecording();
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }
      
      // Sauvegarder l'uniqueid si l'appel se termine avant la création de mission
      saveCallUniqueidIfNeeded();
      
      setCurrentSipSession(null);
      setIncomingCall(null);
      // Ne pas réinitialiser le ref ici car saveCallRecording peut encore l'utiliser
    });
  };

  // Fonction pour démarrer l'enregistrement
  const startCallRecording = async (session) => {
    try {
      const pc = session.connection;
      if (!pc) return;

      const senders = pc.getSenders();
      const localAudioTrack = senders.find(s => s.track && s.track.kind === 'audio')?.track;

      const receivers = pc.getReceivers();
      const remoteAudioTrack = receivers.find(r => r.track && r.track.kind === 'audio')?.track;

      if (!localAudioTrack && !remoteAudioTrack) return;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const destination = audioContext.createMediaStreamDestination();

      if (localAudioTrack) {
        const localStream = new MediaStream([localAudioTrack]);
        const localSource = audioContext.createMediaStreamSource(localStream);
        localSource.connect(destination);
      }

      if (remoteAudioTrack) {
        const remoteStream = new MediaStream([remoteAudioTrack]);
        const remoteSource = audioContext.createMediaStreamSource(remoteStream);
        remoteSource.connect(destination);
      }

      const recordingStream = destination.stream;
      callRecordingStreamRef.current = recordingStream;
      const chunks = [];
      callRecordingChunksRef.current = chunks;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/ogg';

      const recorder = new MediaRecorder(recordingStream, { mimeType });
      
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
          callRecordingChunksRef.current = [...chunks];
        }
      };

      recorder.onstop = async () => {
        await saveCallRecording(session, chunks, mimeType);
        audioContext.close();
        callRecordingStreamRef.current = null;
        callRecordingChunksRef.current = [];
        callRecordingRef.current = null;
      };

      recorder.start(1000);
      callRecordingRef.current = recorder;
    } catch (error) {
      console.error('❌ Erreur lors du démarrage de l\'enregistrement:', error);
    }
  };

  // Fonction pour arrêter l'enregistrement
  const stopCallRecording = async () => {
    const recorder = callRecordingRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch (error) {
        console.error('❌ Erreur lors de l\'arrêt de l\'enregistrement:', error);
        callRecordingRef.current = null;
        callRecordingChunksRef.current = [];
        callRecordingStreamRef.current = null;
      }
    }
  };

  // Fonction pour sauvegarder l'enregistrement
  const saveCallRecording = async (session, chunks, mimeType) => {
    try {
      if (chunks.length === 0) return;

      const blob = new Blob(chunks, { type: mimeType });
      
      // Récupérer l'uniqueid depuis le ref (accès immédiat) ou depuis callsContext
      let uniqueid = currentCallUniqueidRef.current;
      if (!uniqueid && callsContext?.incomingCall?.uniqueid) {
        uniqueid = callsContext.incomingCall.uniqueid;
        currentCallUniqueidRef.current = uniqueid; // Mettre à jour le ref
      }
      // Fallback si toujours pas d'uniqueid
      if (!uniqueid) {
        uniqueid = session.id || `call-${Date.now()}`;
        console.warn('⚠️ Uniqueid non trouvé, utilisation du fallback:', uniqueid);
      }
      
      // Utiliser l'uniqueid comme nom de fichier (sans extension, juste l'uniqueid)
      const filename = uniqueid;

      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Audio = reader.result;
          
          const response = await fetch(`${BASE_URL}/api/audio/upload`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              audioData: base64Audio,
              filename: filename,
              date: new Date().toISOString(),
            }),
          });

          const result = await response.json();

          if (result.success || response.ok) {
            const audioPath = result.remotePath || result.filename || result.path || filename;
            
            // Sauvegarder le chemin du fichier audio avec l'uniqueid
            setCallAudioPath(audioPath);
            
            // Sauvegarder aussi dans localStorage pour utilisation ultérieure
            if (uniqueid) {
              try {
                const audioInfo = {
                  uniqueid: uniqueid,
                  audioPath: audioPath,
                  timestamp: new Date().toISOString(),
                };
                localStorage.setItem(`call_audio_${uniqueid}`, JSON.stringify(audioInfo));
              } catch (e) {
                console.error('Erreur sauvegarde chemin audio:', e);
              }
            }
            
            toast({
              title: 'Enregistrement sauvegardé',
              description: `Fichier: ${audioPath}`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error('❌ Erreur lors de l\'envoi de l\'enregistrement:', error);
        }
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de l\'enregistrement:', error);
    }
  };

  // Connexion téléphone
  const handleConnectPhone = async () => {
    try {
      if (!phoneExtension) {
        toast({
          title: 'Extension requise',
          description: 'Merci de saisir l\'extension SIP.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const ext = String(phoneExtension).trim();
      const passToUse = String(config.pbx.sipPassword || '').trim();
      const pbxHost = config.pbx.host;
      const domain = sipDomain.trim() || pbxHost;

      if (!passToUse) {
        toast({
          title: 'Configuration manquante',
          description: 'Définissez REACT_APP_PBX_SIP_PASSWORD dans le fichier .env (mot de passe SIP du PBX).',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      setPhoneExtension(ext);

      if (sipUA) {
        try {
          sipUA.stop();
          sipUA.unregister();
        } catch (e) {}
        setSipUA(null);
        setSipRegistered(false);
        setIsPhoneConnected(false);
      }

      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('softphone_extension', ext);
        }
        if (callsContext?.setExtension) {
          callsContext.setExtension(ext);
        }
      } catch (e) {}

      const JsSIPLib = JsSIP;

      if (!JsSIPLib.__patchedKeepAlive) {
        try {
          const WSI = JsSIPLib.WebSocketInterface;
          if (WSI && WSI.prototype && WSI.prototype._onMessage) {
            const originalOnMessage = WSI.prototype._onMessage;
            WSI.prototype._onMessage = function (event) {
              const data = event && event.data;
              if (typeof data === 'string' && data.trim() === '-1') {
                return;
              }
              return originalOnMessage.call(this, event);
            };
            JsSIPLib.__patchedKeepAlive = true;
          }
        } catch (e) {}
      }

      try {
        const wsUrl = getPbxWsUrl();
        console.log('[PhoneWidget] WebSocket SIP:', wsUrl);
        const socketWebRTC = new JsSIPLib.WebSocketInterface(wsUrl);

        const sipUri = `sip:${ext}@${domain}`;
        const configuration = {
          sockets: [socketWebRTC],
          uri: sipUri,
          password: passToUse,
          register: true,
          session_timers: false,
        };

        const ua = new JsSIPLib.UA(configuration);

        ua.on('registered', () => {
          setSipRegistered(true);
          setIsPhoneConnected(true);
          onPhoneModalClose();
          toast({
            title: 'Téléphone connecté',
            description: `Extension ${ext} connectée avec succès`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        });

        ua.on('registrationFailed', (e) => {
          console.error('❌ Échec enregistrement SIP:', e);
          setSipRegistered(false);
          setIsPhoneConnected(false);
          toast({
            title: 'Erreur d\'enregistrement',
            description: e.cause || 'Impossible de s\'enregistrer sur le PBX',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        });

        ua.on('newRTCSession', (e) => {
          const session = e.session;
          if (e.originator === 'remote') {
            const caller = session.remote_identity?.uri?.user || 'Inconnu';
            setIncomingCall({ session, from: caller });
          }
        });

        ua.on('unregistered', () => {
          setSipRegistered(false);
          setIsPhoneConnected(false);
        });

        ua.start();
        setSipUA(ua);
      } catch (err) {
        console.error('Erreur initialisation JsSIP:', err);
        setSipRegistered(false);
        setIsPhoneConnected(false);
        toast({
          title: 'Erreur WebRTC',
          description: `Impossible d'initialiser JsSIP: ${err.message || 'Erreur inconnue'}`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Erreur connexion téléphone:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la connexion au téléphone.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Répondre à un appel entrant
  const answerIncomingCall = async () => {
    if (!incomingCall?.session) {
      toast({
        title: 'Erreur',
        description: 'Pas d\'appel entrant à répondre.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const session = incomingCall.session;

    try {
      stopRing();
      attachSessionEvents(session);
      
      let localStream;
      try {
        localStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
      } catch (e) {
        toast({
          title: 'Erreur micro',
          description: 'Accès au microphone refusé.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      session.answer({
        mediaConstraints: { audio: true, video: false },
        mediaStream: localStream,
        pcConfig: {
          iceServers: [],
          bundlePolicy: 'max-bundle',
          rtcpMuxPolicy: 'require',
        },
      });

      setCurrentSipSession(session);
      
      // Récupérer l'uniqueid et callerId depuis CallsContext
      const amiCall = callsContext?.incomingCall;
      if (amiCall && amiCall.uniqueid) {
        const uniqueid = amiCall.uniqueid;
        setCurrentCallUniqueid(uniqueid);
        currentCallUniqueidRef.current = uniqueid; // Stocker dans le ref pour accès immédiat
        setCurrentCallerId(amiCall.callerId || incomingCall.from);
      } else {
        // Fallback : utiliser l'ID de session comme identifiant
        const fallbackId = session.id || `webrtc-${Date.now()}`;
        setCurrentCallUniqueid(fallbackId);
        currentCallUniqueidRef.current = fallbackId;
        setCurrentCallerId(incomingCall.from);
        console.warn('⚠️ Uniqueid non trouvé dans CallsContext, utilisation fallback:', fallbackId);
      }

      setIncomingCall(null);

      if (incomingBeepRef.current) {
        clearInterval(incomingBeepRef.current);
        incomingBeepRef.current = null;
      }

      // Afficher le modal pour lier à une mission
      onLinkMissionModalOpen();

      toast({
        title: 'Appel accepté',
        description: 'Vous pouvez maintenant parler.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('❌ Erreur lors de la réponse:', err);
      toast({
        title: 'Erreur',
        description: err.message || 'Impossible de répondre à l\'appel.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
      try {
        session.terminate();
      } catch (e) {}
      setIncomingCall(null);
      setCurrentSipSession(null);
    }
  };

  // Sauvegarder l'uniqueid si l'appel se termine avant la création de mission
  const saveCallUniqueidIfNeeded = () => {
    // Utiliser le ref pour avoir l'uniqueid immédiatement
    const uniqueid = currentCallUniqueidRef.current || currentCallUniqueid;
    if (uniqueid && currentCallerId) {
      try {
        // Récupérer le chemin audio si disponible
        let audioPath = null;
        try {
          const audioInfoStr = localStorage.getItem(`call_audio_${uniqueid}`);
          if (audioInfoStr) {
            const audioInfo = JSON.parse(audioInfoStr);
            audioPath = audioInfo.audioPath;
          }
        } catch (e) {
          console.warn('Impossible de récupérer le chemin audio:', e);
        }

        // Vérifier si une mission est déjà en cours de création
        const pendingCall = localStorage.getItem('pending_call_for_mission');
        if (!pendingCall) {
          // Sauvegarder pour liaison future
          const callData = {
            uniqueid: uniqueid,
            callerId: currentCallerId,
            audioPath: audioPath, // Inclure le chemin audio
            timestamp: new Date().toISOString(),
          };
          localStorage.setItem('pending_call_for_mission', JSON.stringify(callData));
        }
      } catch (e) {
        console.error('Erreur sauvegarde appel:', e);
      }
    }
  };

  // Rejeter un appel entrant
  const rejectIncomingCall = () => {
    if (!incomingCall?.session) return;
    
    const channelToHangup = incomingCall.channel || callsContext?.incomingCall?.channel;
    
    try {
      stopRing();
      incomingCall.session.terminate();
    } catch (e) {
      console.error('Erreur terminate:', e);
    }
    
    if (incomingBeepRef.current) {
      clearInterval(incomingBeepRef.current);
      incomingBeepRef.current = null;
    }

    if (channelToHangup && callsContext?.rejectCall) {
      try {
        callsContext.rejectCall(channelToHangup).catch(err => {
          console.error('Erreur rejet AMI:', err);
        });
      } catch (err) {
        console.error('Erreur rejet AMI:', err);
      }
    }

    setIncomingCall(null);
  };

  // Raccrocher
  const hangupWebRTC = () => {
    if (currentSipSession) {
      try {
        stopCallRecording();
        const pc = currentSipSession.connection;
        if (pc) {
          pc.getSenders().forEach(sender => {
            if (sender.track) {
              sender.track.stop();
            }
          });
        }
        currentSipSession.terminate();
      } catch (e) {
        console.error('❌ Erreur terminate:', e);
      }
      
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }
      
      // Sauvegarder l'uniqueid si l'appel se termine avant la création de mission
      saveCallUniqueidIfNeeded();
      
      setCurrentSipSession(null);
      setIncomingCall(null);
    }
  };

  // Appel sortant
  const callNumber = async () => {
    const number = String(dialNumber || '').trim();
    if (!number) {
      toast({
        title: 'Numéro manquant',
        description: 'Veuillez saisir un numéro à appeler.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!sipUA || !sipRegistered) {
      toast({
        title: 'Téléphone non connecté',
        description: 'Connectez d\'abord le téléphone avant d\'appeler.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {

      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      const options = {
        mediaConstraints: { audio: true, video: false },
        mediaStream: localStream,
        pcConfig: {
          iceServers: [],
          bundlePolicy: 'max-bundle',
          rtcpMuxPolicy: 'require',
          iceTransportPolicy: 'all',
        },
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false,
        },
      };

      const targetUri = `sip:${number}@${config.pbx.host}`;
      const session = sipUA.call(targetUri, options);
      setCurrentSipSession(session);
      attachSessionEvents(session);

      onCallModalClose(); // Fermer le modal après avoir lancé l'appel
      setDialNumber(''); // Réinitialiser le champ

      toast({
        title: 'Appel en cours',
        description: `Appel vers ${number}...`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('❌ Erreur appel sortant:', error);
      toast({
        title: 'Erreur appel',
        description: error.message || 'Impossible de lancer l\'appel.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Transfert d'appel (blind transfer vers un autre numéro)
  const transferCall = () => {
    const number = String(dialNumber || '').trim();

    if (!currentSipSession) {
      toast({
        title: 'Aucun appel en cours',
        description: 'Vous devez être en communication pour transférer un appel.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!number) {
      toast({
        title: 'Numéro manquant',
        description: 'Veuillez saisir un numéro vers lequel transférer l\'appel.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const targetUri = `sip:${number}@${config.pbx.host}`;

      // Blind transfer via REFER
      if (typeof currentSipSession.refer === 'function') {
        currentSipSession.refer(targetUri);

        toast({
          title: 'Transfert en cours',
          description: `Transfert de l'appel vers ${number}...`,
          status: 'info',
          duration: 4000,
          isClosable: true,
        });

        // Optionnel : on ferme le modal et on vide le champ
        onCallModalClose();
        setDialNumber('');
      } else {
        toast({
          title: 'Transfert non supporté',
          description: 'La fonction de transfert n\'est pas disponible sur cette session.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('❌ Erreur transfert appel:', error);
      toast({
        title: 'Erreur transfert',
        description: error.message || 'Impossible de transférer l\'appel.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      {/* Élément audio caché pour l'audio distant */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />

      {/* Bouton Connect Phone */}
      <Button
        variant="ghost"
        bg="white"
        border="1px solid #E5E5E5"
        px="10px"
        py="6px"
        minW="160px"
        h="40px"
        borderRadius="6px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        onClick={onPhoneModalOpen}
        _hover={{ bg: "gray.50" }}
        position="relative"
      >
        <HStack spacing="8px" justify="center" w="100%">
          <Icon
            as={isPhoneConnected ? MdPhoneEnabled : MdPhone}
            color={isPhoneConnected ? "#10b981" : "#656C7A"}
            w="18px"
            h="18px"
          />
          <Text
            fontSize="12px"
            fontWeight="700"
            color={isPhoneConnected ? "#10b981" : "#455879"}
            whiteSpace="nowrap"
          >
            {isPhoneConnected ? "Connecté" : "Connecter"}
          </Text>
        </HStack>
        {isPhoneConnected && (
          <Badge
            position="absolute"
            top="-2px"
            right="-2px"
            bg="#10b981"
            borderRadius="full"
            w="8px"
            h="8px"
            border="2px solid white"
          />
        )}
      </Button>

      {/* Bouton Appeler / Transférer - visible seulement si téléphone connecté et pas d'appel entrant en attente */}
      {isPhoneConnected && !incomingCall && (
        <Button
          variant="ghost"
          bg="white"
          border="1px solid #E5E5E5"
          px="10px"
          py="6px"
          minW="160px"
          h="40px"
          borderRadius="6px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={onCallModalOpen}
          _hover={{ bg: currentSipSession ? "blue.50" : "blue.50" }}
        >
          <HStack spacing="8px" justify="center" w="100%">
            <Icon
              as={MdCallMade}
              color={currentSipSession ? "#2563eb" : "#3b82f6"}
              w="18px"
              h="18px"
            />
            <Text
              fontSize="12px"
              fontWeight="700"
              color="#455879"
              whiteSpace="nowrap"
            >
              Appeler / Transférer
            </Text>
          </HStack>
        </Button>
      )}

      {/* Bouton Répondre si appel entrant */}
      {incomingCall && !currentSipSession && (
        <Button
          variant="ghost"
          bg="white"
          border="1px solid #E5E5E5"
          px="10px"
          py="6px"
          minW="160px"
          h="40px"
          borderRadius="6px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={answerIncomingCall}
          _hover={{ bg: "green.50" }}
          borderColor="green.300"
        >
          <HStack spacing="8px" justify="center" w="100%">
            <Icon as={MdPhone} color="#10b981" w="18px" h="18px" />
            <Text
              fontSize="12px"
              fontWeight="700"
              color="#10b981"
              whiteSpace="nowrap"
            >
              Répondre
            </Text>
          </HStack>
        </Button>
      )}

      {/* Bouton Raccrocher si appel en cours */}
      {currentSipSession && (
        <Button
          variant="ghost"
          bg="white"
          border="1px solid #E5E5E5"
          px="10px"
          py="6px"
          minW="160px"
          h="40px"
          borderRadius="6px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={hangupWebRTC}
          _hover={{ bg: "red.50" }}
          borderColor="red.300"
        >
          <HStack spacing="8px" justify="center" w="100%">
            <Icon as={MdPhoneDisabled} color="#ef4444" w="18px" h="18px" />
            <Text
              fontSize="12px"
              fontWeight="700"
              color="#ef4444"
              whiteSpace="nowrap"
            >
              Raccrocher
            </Text>
          </HStack>
        </Button>
      )}

      {/* Modal pour connecter le téléphone */}
      <Modal isOpen={isPhoneModalOpen} onClose={onPhoneModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Connexion Téléphone</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text fontSize="12px" color="#656C7A" mb="12px">
              Saisissez uniquement l&apos;extension. Le mot de passe SIP est lu depuis la variable d&apos;environnement REACT_APP_PBX_SIP_PASSWORD. Si la connexion WSS échoue (certificat), installez le certificat PBX sur le poste puis réessayez.
            </Text>
            <HStack mb="14px" spacing="10px" justify="flex-start" flexWrap="wrap">
              <Button
                as="a"
                href="/EC74D71E4A5C.crt"
                download
                size="xs"
                variant="outline"
              >
                Télécharger le certificat PBX
              </Button>
              <Text fontSize="12px" color="#656C7A">
                Ensuite: double‑clic → “Installer le certificat” (Ordinateur local → Autorités racines de confiance).
              </Text>
            </HStack>
            <FormControl>
              <FormLabel>Extension</FormLabel>
              <Input
                value={phoneExtension}
                onChange={(e) => setPhoneExtension(e.target.value)}
                placeholder="303"
              />
            </FormControl>
         
            <Flex mt={6} justify="flex-end" gap={2}>
              <Button onClick={onPhoneModalClose}>Annuler</Button>
              <Button colorScheme="blue" onClick={handleConnectPhone}>
                Connecter
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal pour appeler un numéro */}
      <Modal isOpen={isCallModalOpen} onClose={onCallModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Appeler un numéro</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Numéro à appeler</FormLabel>
              <Input
                value={dialNumber}
                onChange={(e) => setDialNumber(e.target.value)}
                placeholder="Entrez un numéro (ex: 303)"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    // Si un appel est en cours, Enter lance le transfert, sinon un appel sortant
                    if (currentSipSession) {
                      transferCall();
                    } else {
                      callNumber();
                    }
                  }
                }}
                autoFocus
              />
            </FormControl>
            <Flex mt={6} justify="flex-end" gap={2}>
              <Button onClick={onCallModalClose}>Annuler</Button>
              {/* Bouton Appeler si aucun appel en cours */}
              {!currentSipSession && (
                <Button
                  colorScheme="green"
                  onClick={callNumber}
                  isDisabled={!dialNumber.trim()}
                >
                  Appeler
                </Button>
              )}
              {/* Bouton Transférer si un appel est en cours */}
              {currentSipSession && (
                <Button
                  colorScheme="blue"
                  onClick={transferCall}
                  isDisabled={!dialNumber.trim()}
                >
                  Transférer l'appel
                </Button>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal pour lier l'appel à une mission */}
      <LinkCallToMissionModal
        isOpen={isLinkMissionModalOpen}
        onClose={onLinkMissionModalClose}
        uniqueid={currentCallUniqueid}
        callerId={currentCallerId}
        isCallActive={!!currentSipSession}
        onLinkSuccess={(missionId) => {
          // Réinitialiser les états
          setCurrentCallUniqueid(null);
          setCurrentCallerId(null);
        }}
      />
    </>
  );
}

